import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2, Loader2 } from "lucide-react";

// Same-origin PHP handler shipped alongside the static build.
// Override at build time with REACT_APP_ENQUIRY_ENDPOINT if the site
// lives in a subfolder or the API is hosted elsewhere.
const ENDPOINT = process.env.REACT_APP_ENQUIRY_ENDPOINT || "/api/enquiry.php";
const EASE = [0.16, 1, 0.3, 1];
const PRODUCT_OPTIONS = ["Tees", "Hoodies", "Headwear", "Posters", "Accessories"];

const EMPTY = {
  band_name: "",
  email: "",
  origin_country: "",
  tour_start: "",
  destination: "",
  product_types: [],
  quantity: "",
  notes: "",
  company: "", // honeypot — real users leave this blank
};

const inputCls =
  "w-full border border-zinc-300 bg-white px-4 py-3 text-sm outline-none transition-all focus:border-black focus:ring-2 focus:ring-black/10 placeholder:text-zinc-400";

export default function EnquiryForm({ preselect }) {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    if (preselect?.length) {
      setForm((f) => ({ ...f, product_types: [...new Set([...f.product_types, ...preselect])] }));
    }
  }, [preselect]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleType = (t) =>
    setForm((f) => ({
      ...f,
      product_types: f.product_types.includes(t)
        ? f.product_types.filter((x) => x !== t)
        : [...f.product_types, t],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(ENDPOINT, form, {
        headers: { "Content-Type": "application/json" },
      });
      if (!data || data.status !== "success" || !data.id) {
        throw new Error(data?.error || "Unexpected response");
      }
      setReceipt(data.id);
      toast.success(`Enquiry received — reference ${data.id}`);
      setForm(EMPTY);
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          "Could not send enquiry. Please try again."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="enquiry" data-testid="enquiry-form-section" className="border-t border-zinc-200 bg-tour-zinc/60 py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.3fr]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">Start an enquiry</p>
          <h2 className="font-display text-3xl font-extrabold tracking-tighter sm:text-4xl lg:text-5xl">
            Tell us where <span className="font-serif italic font-medium">you're playing.</span>
          </h2>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-zinc-600 sm:text-base">
            Domestic run or a full international routing — send the details and we'll come back
            with designs, a quote, and a fulfillment plan for every date.
          </p>
          <div className="mt-10 space-y-3 font-mono text-xs uppercase tracking-widest text-zinc-500">
            <p>Response within 24 hours</p>
            <p>International + domestic routing</p>
            <p>Design, print and delivery handled</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          className="border border-zinc-200 bg-white p-6 sm:p-10"
        >
          {receipt ? (
            <div data-testid="enquiry-confirmation" className="flex flex-col items-start gap-5 py-8">
              <CheckCircle2 className="h-10 w-10 text-tour-red" />
              <h3 className="font-display text-2xl font-bold tracking-tight">Enquiry received.</h3>
              <p className="text-sm text-zinc-600">
                Your reference is <span className="font-mono font-bold text-tour-void">{receipt}</span>. A confirmation
                email is on its way to you, and we'll be in touch within 24 hours with next steps.
              </p>
              <button
                data-testid="enquiry-new-btn"
                onClick={() => setReceipt(null)}
                className="border border-zinc-300 px-6 py-3 font-mono text-xs uppercase tracking-widest transition-colors hover:border-black"
              >
                Send another enquiry
              </button>
            </div>
          ) : (
            <form data-testid="enquiry-form" onSubmit={submit} className="space-y-5">
              {/* Honeypot: hidden from users, bots tend to fill it */}
              <div aria-hidden="true" className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden">
                <label>
                  Company
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.company}
                    onChange={set("company")}
                  />
                </label>
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Band / Artist name *</label>
                  <input data-testid="enquiry-band-name-input" required value={form.band_name} onChange={set("band_name")} placeholder="The Midnight Static" className={inputCls} />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Contact email *</label>
                  <input data-testid="enquiry-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="band@example.com" className={inputCls} />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Origin country *</label>
                  <input data-testid="enquiry-origin-input" required value={form.origin_country} onChange={set("origin_country")} placeholder="United Kingdom" className={inputCls} />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Tour start date</label>
                  <input data-testid="enquiry-tour-start-input" type="date" value={form.tour_start} onChange={set("tour_start")} className={inputCls} />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Destination / venue</label>
                  <input data-testid="enquiry-destination-input" value={form.destination} onChange={set("destination")} placeholder="Berlin — Columbiahalle" className={inputCls} />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Estimated quantity</label>
                  <input data-testid="enquiry-quantity-input" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 150 tees, 60 hoodies" className={inputCls} />
                </div>
              </div>

              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Product types</label>
                <div className="flex flex-wrap gap-2">
                  {PRODUCT_OPTIONS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      data-testid={`enquiry-product-${t.toLowerCase()}`}
                      onClick={() => toggleType(t)}
                      className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors ${
                        form.product_types.includes(t)
                          ? "border-tour-void bg-tour-void text-white"
                          : "border-zinc-300 text-zinc-600 hover:border-black hover:text-black"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Design / tour notes</label>
                <textarea data-testid="enquiry-notes-input" rows={4} value={form.notes} onChange={set("notes")} placeholder="Routing, artwork status, sizes split, deadlines..." className={inputCls} />
              </div>

              <button
                data-testid="submit-enquiry-btn"
                type="submit"
                disabled={sending}
                className="group flex w-full items-center justify-center gap-2 bg-tour-void px-7 py-4 font-mono text-xs uppercase tracking-widest text-white transition-colors hover:bg-tour-red disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
                {sending ? "Sending..." : "Send enquiry"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
